import VueRouter from 'vue-router';

import { validateContentTypeUuid, validateWorkspace } from './preGuards.js';
import store from '@/store';

// Layout
import MasterLayout from '@/layouts/MasterLayout.vue';
import ErrorPage from '@/layouts/ErrorPage.vue';

// Dynamic Components
import DynamicForm from '@/components/dynamic/form/Index.vue';
import DynamicList from '@/components/dynamic/list/Index.vue';

// Pages
import Home from '@/components/layouts/Home.vue';
import FindCandidate from '@/components/application/components/FindCandidate.vue';
import AddCandidate from '@/components/application/components/AddCandidate.vue';

import { getLogoutUrl, getLoginUrl } from '@/utils/auth';

const allowedModes = {
  create: 'creer',
  view: 'vue',
  edit: 'editer'
};

const cantCreate = [
  'offres'
];

const testWorkspaceForbiddenRoutes = [
  'home',
  'candidates-list',
  'candidature-spontanee-list',
  'candidatures',
  'offer-candidatures'
];

const routes = [
  {
    path: '/',
    component: MasterLayout,
    redirect: () => {
      const workspace = store.state.activeWorkspace;

      if (workspace === 'spontaneous') {
        return { name: 'candidature-spontanee-list' };
      }

      if (workspace === store.state.centralizedWorkspace) {
        return { name: 'model-offers-list' };
      }

      return { name: 'home' };
    },
    children: [
      {
        path: '',
        name: 'root',
        redirect: () => {
          const workspace = store.state.activeWorkspace;
          if (workspace === 'spontaneous') {
            return { name: 'candidature-spontanee-list' };
          }

          if (workspace === store.state.centralizedWorkspace) {
            return { name: 'model-offers-list' };
          }

          return { name: 'home' };
        }
      },
      {
        path: 'dashboard',
        name: 'home',
        component: Home
      },
      {
        path: '/candidats',
        name: 'candidates-list',
        component: DynamicList,
        meta: { currentDeepRoute: 1, listComponent: 'DataIterable' },
        props: () => ({ contentTypeLabel: 'candidats' }),
        children: [
          {
            path: 'rechercher/:offerUuid',
            name: 'rechercher-candidate',
            component: FindCandidate,
            meta: {
              currentDeepRoute: 2,
              onWorkspaceChangeRedirectTo: () => ({ name: 'candidates-list' })

            }
          },
          {
            path: 'creer',
            name: 'add-candidate',
            component: AddCandidate,
            meta: { currentDeepRoute: 2 }
          },
          {
            path: ':candidateUuid',
            name: 'candidate',
            component: DynamicForm,
            meta: {
              currentDeepRoute: 2,
              onWorkspaceChangeRedirectTo: () => ({ name: 'candidates-list' })
            },
            props: route => ({
              mode: route.params.mode,
              uuid: route.params.uuid
            })
          }
        ]
      },
      {
        path: '/candidatures-spontanees',
        name: 'candidature-spontanee-list',
        component: DynamicList,
        beforeEnter: (to, _from, next) => validateWorkspace(to.name, store.state.activeWorkspace, store.getters.isCentralizedWorkspace, next),
        meta: { currentDeepRoute: 1, listComponent: 'DataIterable' },
        props: () => ({ contentTypeLabel: 'candidatures-spontanees' }),
        children: [
          {
            path: ':candidatureUuid',
            name: 'candidature-spontanee',
            component: DynamicForm,
            meta: { currentDeepRoute: 2 },
            props: route => ({
              mode: route.params.mode,
              uuid: route.params.uuid
            })
          }
        ]
      },
      {
        path: 'candidatures',
        name: 'candidatures',
        component: DynamicList,
        meta: { currentDeepRoute: 1, contentType: 'candidatures', listComponent: 'DataIterable', childRouteName: 'candidature' },
        props: () => ({ contentTypeLabel: 'candidatures' }),
        children: [
          {
            path: ':candidatureUuid',
            name: 'candidature',
            component: DynamicForm,
            meta: {
              currentDeepRoute: 2,
              contentType: 'candidatures',
              onWorkspaceChangeRedirectTo: () => ({ name: 'candidatures' })
            },
            beforeEnter: (to, _from, next) => validateContentTypeUuid(to.params.candidatureUuid, 'application', next),
            props: route => ({
              mode: route.params.mode || 'vue',
              uuid: route.params.candidatureUuid
            })
          }
        ]
      },
      {
        path: 'modeles-d-offres',
        name: 'model-offers-list',
        component: DynamicList,
        beforeEnter: (to, _from, next) => validateWorkspace(to.name, store.state.activeWorkspace, store.getters.isCentralizedWorkspace, next),
        meta: { currentDeepRoute: 1, listComponent: 'DataTable' },
        props: () => ({ contentTypeLabel: 'modeles-d-offres' }),
        children: [
          {
            path: ':mode/:uuid?',
            name: 'model_offer',
            component: DynamicForm,
            meta: { currentDeepRoute: 2 },
            props: route => ({
              mode: route.params.mode,
              uuid: route.params.uuid
            })
          }
        ]
      },
      {
        path: 'offres',
        name: 'offers',
        component: DynamicList,
        meta: { currentDeepRoute: 1, listComponent: 'DataTable' },
        props: route => ({ contentTypeLabel: ['offers', 'offer'].includes(route.name) ? 'offres' : 'candidatures' }),
        children: [
          {
            path: ':offerUuid/candidatures',
            name: 'offer-candidatures',
            component: DynamicList,
            meta: {
              currentDeepRoute: 1,
              contentTypeLabel: 'candidatures',
              listComponent: 'DataIterable',
              childRouteName: 'offer-candidature',
              onWorkspaceChangeRedirectTo: () => ({ name: 'offers' })
            },
            beforeEnter: (to, _from, next) => {
              store.dispatch('resetFilters');
              return validateContentTypeUuid(to.params.offerUuid, 'offer', next);
            },
            children: [
              {
                path: ':candidatureUuid',
                name: 'offer-candidature',
                component: DynamicForm,
                meta: {
                  currentDeepRoute: 2,
                  contentTypeLabel: 'candidatures',
                  onWorkspaceChangeRedirectTo: () => ({ name: 'offers' })
                },
                beforeEnter: (to, _from, next) => validateContentTypeUuid(to.params.candidatureUuid, 'application', next),
                props: route => ({
                  mode: route.params.mode || 'vue',
                  uuid: route.params.candidatureUuid
                })
              }
            ]
          },
          {
            path: ':mode/:uuid?',
            name: 'offer',
            component: DynamicForm,
            meta: {
              currentDeepRoute: 2,
              onWorkspaceChangeRedirectTo: route => {
                if (['creer'].includes(route.params.mode)) {
                  return null;
                }

                return { name: 'offers' };
              }
            },
            props: route => ({
              mode: route.params.mode,
              uuid: route.params.uuid
            })
          }
        ]
      },
      {
        path: 'besoins',
        name: 'jobs',
        component: DynamicList,
        meta: { currentDeepRoute: 1, listComponent: 'DataTable' },
        props: () => ({ contentTypeLabel: 'besoins' }),
        children: [
          {
            path: ':mode/:uuid?',
            name: 'job',
            component: DynamicForm,
            meta: {
              currentDeepRoute: 2,
              onWorkspaceChangeRedirectTo: route => {
                if (['creer'].includes(route.params.mode)) {
                  return null;
                }

                return { name: 'jobs' };
              }
            },
            props: route => ({
              mode: route.params.mode,
              uuid: route.params.uuid
            })
          }
        ]
      },
      {
        path: 'vivier',
        component: { render: c => c('router-view') },
        children: [
          {
            path: '',
            name: 'vivier-search',
            component: () => import('@/views/vivier/VivierSearch.vue')
          },
          {
            path: ':uuid',
            name: 'vivier-show',
            component: () => import('@/views/vivier/VivierShow.vue'),
            props: true
          }
        ]
      },
      {
        path: ':errorCode(\\d+)', // Only match numbers
        name: 'error',
        component: ErrorPage,
        props: route => ({ code: parseInt(route.params.errorCode) })
      },
      {
        path: '*',
        name: 'not-found',
        component: ErrorPage,
        props: { code: 404 }
      }
    ]
  }
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
});

// Add this navigation guard here
router.beforeEach((to, from, next) => {
  // Check if the path contains multiple consecutive slashes
  if (to.path.includes('//')) {
    // Replace multiple slashes with a single slash
    const cleanedPath = to.path.replace(/\/+/g, '/');

    // Redirect to the cleaned URL, preserving query params and hash
    return next({
      path: cleanedPath,
      query: to.query,
      hash: to.hash,
      replace: true // Use replace to avoid polluting browser history
    });
  }

  // If the path is clean, proceed as normal
  return next();
});

router.beforeResolve(async (to, from, next) => {
  // Initialize user if not already done
  if (!store.state.user) {
    const userInitialized = await store.dispatch('initializeUser');
    if (!userInitialized) {
      window.location.href = getLoginUrl(window.location.origin, process.env.VUE_APP_SSO_CLIENT_ID);
      return next(false);
    }
  }

  // Redirect to logout if user has no workspace access
  if (!store.state.user?.writeWorkspaces && !store.state.user?.readWorkspaces) {
    window.location.href = getLogoutUrl(window.location.origin, process.env.VUE_APP_SSO_CLIENT_ID, window.location.origin);
    return next(false);
  }

  // Allow error page
  if (to.name === 'error') {
    return next();
  }

  // Block access to unauthorized routes
  if (!store.getters.canAccessRoute(to.path) && to.path !== '/dashboard' && to.name !== 'error') {
    return next({ name: 'error', params: { errorCode: 403 } });
  }

  if (['job', 'offer'].includes(to.name)) {
    // Invalid mode
    if (!Object.values(allowedModes).includes(to.params.mode)) {
      return next({ name: 'error', params: { errorCode: 404 } });
    }

    // View/Edit mode but missing uuid
    if ([allowedModes.view, allowedModes.edit].includes(to.params.mode) && !to.params.uuid) {
      if (!cantCreate.includes(to.params.contentTypeLabel)) {
        return next({
          name: to.name,
          params: {
            contentTypeLabel: to.params.contentTypeLabel,
            mode: allowedModes.create
          }
        });
      }
      return next({
        name: to.params.contentTypeLabel,
        params: { contentTypeLabel: to.params.contentTypeLabel }
      });
    }
  }

  const isWorkspaceSpontaneous = store.state.activeWorkspace === 'spontaneous';
  // const isSpontaneousRoute = to.matched.some(route => route.name === 'candidature-spontanee-list');

  // if (isSpontaneousRoute && store.state.activeWorkspace !== 'spontaneous') {
  //   return next({ name: 'error', params: { errorCode: 404 } });
  // }

  if (isWorkspaceSpontaneous && ['/offres', '/besoins', '/dashboard', '/'].includes(to.path)) {
    return next({ name: 'candidature-spontanee-list' });
  }

  if (store.getters.isCentralizedWorkspace) {
    if (to.matched.some(record => testWorkspaceForbiddenRoutes.includes(record.name))) {
      return next({ name: 'error', params: { errorCode: 404 } });
    }
  }

  return next();
});

export default router;
